import { type Module } from "vuex";
import type { AxiosRequestConfig } from "axios";
import type {
  AgentVoice,
  CompanyRequest,
  DefaultApi,
  WebCallConnectionResponse,
} from "@/api";
import { Companies } from "@/models/companies";
import { Company } from "@/models/company";
import {
  buildCompanySuggestionCatalog,
  type FaqCatalogResponse,
} from "@/models/faq_catalog";
import type { State as RootState } from "@/stores";
export interface State {
  companies?: Companies;
}
export type CompanyAgentPreviewWebCall = WebCallConnectionResponse;
export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    companies: undefined,
  },
  getters: {
    defaultCompany(state): Company | undefined {
      return state.companies?.find((c) => c.is_default) ?? state.companies?.[0];
    },
  },
  actions: {
    // Company-wide candidate FAQ suggestions (curated server catalog). Not cached: the Library page
    // fetches it per view.
    // Company FAQ suggestions are static, client-owned content (see models/faq_catalog.ts);
    // kept as an action so callers stay decoupled from where it comes from.
    getCompanyFaqQuestionCatalog(): Promise<FaqCatalogResponse> {
      return Promise.resolve(buildCompanySuggestionCatalog());
    },
    async getCompany({ commit }, companyId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getCompany(companyId);
      const company = new Company(response.data);
      commit("setCompany", company);
      return company;
    },
    async getCompanies({ commit }) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getCompanies();
      const companies = new Companies(
        response.data.map((company) => new Company(company)),
      );
      commit("appendCompanies", companies);
      return companies;
    },
    async postCompany({}, companyRequest: CompanyRequest) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCompany(companyRequest);
      return new Company(response.data);
    },
    async patchCompany(
      { commit },
      {
        companyId,
        companyRequest,
      }: {
        companyId: number;
        companyRequest: CompanyRequest;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchCompany(companyId, companyRequest);
      const company = new Company(response.data);
      commit("setCompany", company);
      return company;
    },
    async uploadCompanyLogo(
      { commit },
      {
        companyId,
        file,
      }: {
        companyId: number;
        file: File;
      },
    ): Promise<Company> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCompanyLogo(companyId, file);
      const company = new Company(response.data);
      commit("setCompany", company);
      return company;
    },
    async postCompanyVoicePreview(
      {},
      {
        companyId,
        agentVoiceId,
      }: {
        companyId: number;
        agentVoiceId: AgentVoice;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const axiosRequestConfig: AxiosRequestConfig = {
        responseType: "arraybuffer" as const,
      };
      return api.postCompanyVoicePreview(
        agentVoiceId,
        companyId,
        axiosRequestConfig,
      );
    },
    async postCompanyAgentPreviewWebCall(
      {},
      {
        companyId,
      }: {
        companyId: number;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCompanyAgentPreview(companyId);
      return response.data as CompanyAgentPreviewWebCall;
    },
  },
  mutations: {
    setCompany(state, company: Company) {
      if (state.companies === undefined)
        state.companies = new Companies([company]);
      else state.companies = state.companies.update(company);
    },
    appendCompanies(state, companies: Companies) {
      if (state.companies === undefined) state.companies = companies;
      else state.companies = state.companies.append(companies);
    },
  },
};
